import React, { useState, useEffect } from "react";
import { getTutorialConfig } from "../services/tutorial";
import "./OnboardingOverlay.css";

interface OnboardingOverlayNewProps {
  onComplete: () => void;
  onStepComplete: (stepId: string) => void;
  onStepPrevious?: (stepId: string) => void;
  currentStepId: string;
  onNextTask?: () => void;
  currentTaskIndex: number;
  software?: "freecad" | "blender" | "musescore" | string;
  seekToFrame?: (frameIndex: number) => void;
  evaluationReady?: boolean;
}

// Tutorial steps are now loaded from tutorial configuration
const OnboardingOverlayNew: React.FC<OnboardingOverlayNewProps> = ({
  onComplete,
  onStepComplete,
  onStepPrevious,
  currentStepId,
  onNextTask,
  currentTaskIndex,
  software = "freecad",
  seekToFrame,
  evaluationReady = false,
}) => {
  const [highlightElement, setHighlightElement] = useState<HTMLElement | null>(
    null,
  );

  // Get steps from tutorial configuration
  const tutorialConfig = getTutorialConfig(software);
  const ALL_STEPS = tutorialConfig?.steps || [];

  const currentStep = ALL_STEPS.find((step) => step.id === currentStepId);
  const currentStepIndex = ALL_STEPS.findIndex(
    (step) => step.id === currentStepId,
  );
  const totalSteps = ALL_STEPS.length;

  console.log(`[OnboardingOverlayNew] Rendering`, {
    currentStepId,
    currentStepIndex,
    totalSteps,
    stepScope: currentStep?.scope,
    stepTitle: currentStep?.title,
    hasStep: !!currentStep,
  });

  useEffect(() => {
    if (currentStep?.targetSelector) {
      const element = document.querySelector(
        currentStep.targetSelector,
      ) as HTMLElement;

      console.log(`[OnboardingOverlayNew] Looking for target selector`, {
        stepId: currentStep.id,
        selector: currentStep.targetSelector,
        scope: currentStep.scope,
        found: !!element,
      });

      setHighlightElement(element);
    } else {
      console.log(`[OnboardingOverlayNew] No target selector for step`, {
        stepId: currentStep?.id,
        scope: currentStep?.scope,
      });
      setHighlightElement(null);
    }
  }, [currentStep]);

  const handleNext = () => {
    if (currentStepIndex < totalSteps - 1) {
      onStepComplete(currentStep!.id);
    } else if (currentStepIndex === totalSteps - 1) {
      // Last step of tutorial
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStepIndex > 0 && onStepPrevious && currentStep) {
      onStepPrevious(currentStep.id);
    }
  };

  // Handle seek-frame action
  useEffect(() => {
    if (currentStep?.action === 'seek-frame' && currentStep.seekFrame !== undefined && seekToFrame) {
      const frameIndex = currentStep.seekFrame;
      // Small delay to ensure the component is ready
      const timeoutId = setTimeout(() => {
        seekToFrame(frameIndex - 1); // Convert to 0-indexed
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [currentStep, seekToFrame]);

  if (!currentStep) return null;

  const getTooltipPosition = () => {
    if (!highlightElement || !currentStep.targetSelector) {
      return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    }

    const rect = highlightElement.getBoundingClientRect();
    const position = currentStep.position || "bottom";
    const baseTooltipWidth = currentStep.maxWidth ?? 380;
    let tooltipWidth = baseTooltipWidth;
    const tooltipHeight = 200;
    const padding = 20;

    let top = 0;
    let left = 0;
    let transform;

    switch (position) {
      case "top":
        top = rect.top - tooltipHeight - 20;
        left = rect.left + rect.width / 2;

        // Clamp width to available viewport space
        tooltipWidth = Math.min(
          tooltipWidth,
          window.innerWidth - padding * 2,
        );

        // Check if tooltip would go off screen when centered
        const leftEdge = left - tooltipWidth / 2;
        const rightEdge = left + tooltipWidth / 2;

        if (leftEdge < padding) {
          // Align to left edge with padding
          left = padding;
          transform = "translateX(0)";
        } else if (rightEdge > window.innerWidth - padding) {
          // Align to right edge with padding
          left = window.innerWidth - tooltipWidth - padding;
          transform = "translateX(0)";
        } else {
          // Center normally
          transform = "translateX(-50%)";
        }

        // Ensure tooltip doesn't go off top
        if (top < padding) {
          // If can't fit above, try to put it below
          const belowTop = rect.bottom + 20;
          if (belowTop + tooltipHeight > window.innerHeight - padding) {
            // Can't fit below either, position it at the top of viewport
            top = padding;
          } else {
            top = belowTop;
          }
        }

        // Final check: ensure tooltip doesn't overflow viewport height
        if (top + tooltipHeight > window.innerHeight - padding) {
          top = Math.max(padding, window.innerHeight - tooltipHeight - padding);
        }
        break;

      case "bottom":
        top = rect.bottom + 20;
        left = rect.left + rect.width / 2;

        tooltipWidth = Math.min(
          tooltipWidth,
          window.innerWidth - padding * 2,
        );

        // Check horizontal bounds
        const bottomLeftEdge = left - tooltipWidth / 2;
        const bottomRightEdge = left + tooltipWidth / 2;

        if (bottomLeftEdge < padding) {
          left = padding;
          transform = "translateX(0)";
        } else if (bottomRightEdge > window.innerWidth - padding) {
          left = window.innerWidth - tooltipWidth - padding;
          transform = "translateX(0)";
        } else {
          transform = "translateX(-50%)";
        }

        // Ensure tooltip doesn't go off bottom
        if (top + tooltipHeight > window.innerHeight - padding) {
          // If can't fit below, try to put it above
          const aboveTop = rect.top - tooltipHeight - 20;
          if (aboveTop < padding) {
            // Can't fit above either, position it at the top of viewport
            top = padding;
          } else {
            top = aboveTop;
          }
        }

        // Final check: ensure tooltip doesn't overflow viewport height
        if (top + tooltipHeight > window.innerHeight - padding) {
          top = Math.max(padding, window.innerHeight - tooltipHeight - padding);
        }
        break;

      case "left":
        // Position tooltip aligned with top of element if near top of screen
        if (rect.top < 100) {
          top = rect.top;
          transform = "translateY(0)";
        } else {
          // Otherwise center it vertically
          top = rect.top + rect.height / 2;
          transform = "translateY(-50%)";
        }

        {
          const available = rect.left - padding - 20;
          if (available > 0) {
            tooltipWidth = Math.min(baseTooltipWidth, Math.max(260, available));
          } else {
            tooltipWidth = baseTooltipWidth;
          }
        }

        left = rect.left - tooltipWidth - 20;
        if (left < padding) {
          left = Math.max(padding, rect.left - tooltipWidth - 20);
        }

        // Ensure tooltip doesn't overflow viewport height
        const effectiveTop = transform === "translateY(-50%)" ? top - tooltipHeight / 2 : top;
        if (effectiveTop < padding) {
          top = padding;
          transform = "translateY(0)";
        } else if (effectiveTop + tooltipHeight > window.innerHeight - padding) {
          top = window.innerHeight - tooltipHeight - padding;
          transform = "translateY(0)";
        }
        break;

      case "right":
        // Position tooltip aligned with top of element if near top of screen
        if (rect.top < 100) {
          top = rect.top;
          transform = "translateY(0)";
        } else {
          // Otherwise center it vertically
          top = rect.top + rect.height / 2;
          transform = "translateY(-50%)";
        }

        tooltipWidth = Math.min(
          tooltipWidth,
          window.innerWidth - padding * 2,
        );

        left = rect.right + 20;

        if (left + tooltipWidth > window.innerWidth - padding) {
          // If can't fit on right, put it on left
          left = Math.max(padding, rect.left - tooltipWidth - 20);
        }

        // Ensure tooltip doesn't overflow viewport height
        const effectiveTopRight = transform === "translateY(-50%)" ? top - tooltipHeight / 2 : top;
        if (effectiveTopRight < padding) {
          top = padding;
          transform = "translateY(0)";
        } else if (effectiveTopRight + tooltipHeight > window.innerHeight - padding) {
          top = window.innerHeight - tooltipHeight - padding;
          transform = "translateY(0)";
        }
        break;

      default:
        return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    }

    if (typeof currentStep.offsetX === 'number') {
      left += currentStep.offsetX;
    }

    if (typeof currentStep.offsetY === 'number') {
      top += currentStep.offsetY;
    }

    // Final viewport boundary check after applying offsets
    // This ensures tooltips remain visible even with custom offsets
    const effectiveFinalTop = transform.includes("translateY(-50%)") ? top - tooltipHeight / 2 : top;
    if (effectiveFinalTop < padding) {
      top = padding;
      transform = transform.replace("translateY(-50%)", "translateY(0)");
    } else if (effectiveFinalTop + tooltipHeight > window.innerHeight - padding) {
      top = window.innerHeight - tooltipHeight - padding;
      transform = transform.replace("translateY(-50%)", "translateY(0)");
    }

    const styles: any = {
      top: `${top}px`,
      left: `${left}px`,
      transform: transform,
      maxWidth: `${tooltipWidth}px`,
      width: `${tooltipWidth}px`,
    };

    // Calculate arrow position when tooltip is not centered
    if (
      transform === "translateX(0)" &&
      highlightElement &&
      (position === "top" || position === "bottom")
    ) {
      const arrowOffset = rect.left + rect.width / 2 - left;
      styles["--arrow-offset"] = `${arrowOffset}px`;
      styles["--arrow-transform"] = "translateX(0)";
    }

    return styles;
  };

  // Always allow interaction during tutorial
  const allowInteraction = currentStep.allowInteraction ?? false;

  return (
    <div
      className={`onboarding-overlay ${allowInteraction ? "allow-interaction" : ""}`}
    >
      <div className="onboarding-backdrop">
        {highlightElement && (
          <div
            className="highlight-cutout"
            style={{
              top: highlightElement.getBoundingClientRect().top - 5,
              left: highlightElement.getBoundingClientRect().left - 5,
              width: highlightElement.getBoundingClientRect().width + 10,
              height: highlightElement.getBoundingClientRect().height + 10,
            }}
          />
        )}
      </div>

      <div
        className="onboarding-tooltip"
        style={getTooltipPosition()}
        data-position={currentStep.position || "bottom"}
      >
        <h3>{currentStep.title}</h3>
        <p>{currentStep.content}</p>

        <div className="tooltip-footer">
          <span className="step-indicator">
            Step {currentStepIndex + 1}/{totalSteps}
          </span>

          <div className="tooltip-buttons">
            {currentStepIndex > 0 && !currentStep.disablePrevious && (
              <button onClick={handlePrevious} className="tooltip-btn secondary">
                {currentStep.previousButtonLabel || "Previous"}
              </button>
            )}

            {currentStep.action === "next" && (
              <button onClick={handleNext} className="tooltip-btn primary">
                {currentStep.nextButtonLabel
                  ? currentStep.nextButtonLabel
                  : currentStepIndex === totalSteps - 1
                  ? "Finish"
                  : "Next"}
              </button>
            )}
            {currentStep.action === "explore" && (
              <button onClick={handleNext} className="tooltip-btn secondary">
                {currentStepIndex === totalSteps - 1 ? "Finish" : "Continue"}
              </button>
            )}
            {currentStep.action === "next-task-button" && (
              <span className="tooltip-instruction">
                Click the highlighted button
              </span>
            )}
            {currentStep.action === "evaluate" && (
              evaluationReady ? (
                <button
                  onClick={handleNext}
                  className="tooltip-btn primary"
                >
                  {currentStep.nextButtonLabel || "Next"}
                </button>
              ) : (
                <span className="tooltip-instruction">Choose your evaluation</span>
              )
            )}
            {currentStep.action === "seek-frame" && (
              <button onClick={handleNext} className="tooltip-btn primary">
                Next
              </button>
            )}
            {currentStep.action === "click" && (
              <span className="tooltip-instruction-text">
                {currentStep.nextButtonLabel || "Click the highlighted element"}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingOverlayNew;
